import { ApolloError } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  ListDashboardsDocument,
  useUpdateDashboardMutation,
} from 'generated/graphql';
import { useCallback, useEffect, useRef } from 'react';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import { useGetUser } from 'v2/core/services/user';
import { updateCachedDashboard } from '../helpers';
import { DashboardActionType } from '../store/dashboardActionType';
import { useDashboardStore } from '../store/useDashboardStore';
import { UpdateDashboardRequest } from '../types';
import { useShowRefreshDashboardModal } from './useShowRefreshDashboardModal';

interface UpdateDashboardProps {
  updateRequest?: UpdateDashboardRequest;
  shouldRefetchAnalyses?: boolean;
}

export const useUpdateDashboard = () => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const [update, { loading, error, called }] = useUpdateDashboardMutation({
    update: updateCachedDashboard,
  });

  const version = useDashboardStore(({ dashboard }) => dashboard.version);
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);

  const versionRef = useRef(version); // to fix stale version inside update of useCallback
  versionRef.current = version;

  const showRefreshDashboardModal = useShowRefreshDashboardModal();

  const { showErrorNotification } = useNotificationContext();

  useEffect(() => {
    if (!error) {
      return;
    }

    if (isErrorCode(error, ErrorCode.UnprocessableEntity)) {
      return;
    }

    showErrorNotification({
      contentComponent: 'Failed to update dashboard',
      props: {
        isClosable: true,
      },
    });
  }, [error, showErrorNotification, showRefreshDashboardModal]);

  const updateDashboard = useCallback(
    async ({ updateRequest, shouldRefetchAnalyses }: UpdateDashboardProps) => {
      if (updateRequest) {
        // TODO: remove this when loading state of useMutation is fixed for React 18. track here: https://github.com/apollographql/apollo-client/issues/9602
        dispatch({
          type: DashboardActionType.SET_VISUAL,
          payload: {
            isUpdateDashboardInProgress: true,
          },
        });
        try {
          return await update({
            variables: {
              tenantID,
              userID,
              ...updateRequest,
              version: updateRequest.version ?? versionRef.current,
            },
            refetchQueries: shouldRefetchAnalyses
              ? [ListDashboardsDocument]
              : undefined,
          });
        } catch (error) {
          if (
            error instanceof ApolloError &&
            isErrorCode(error, ErrorCode.UnprocessableEntity)
          ) {
            showRefreshDashboardModal();
            return;
          }
        } finally {
          dispatch({
            type: DashboardActionType.SET_VISUAL,
            payload: {
              isUpdateDashboardInProgress: false,
            },
          });
        }
      }
    },
    [tenantID, update, userID, showRefreshDashboardModal, dispatch],
  );

  return {
    updateDashboard,
    loading: loading,
    error,
    called,
  };
};
